import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CustomButton } from "../../../atoms/CustomButton";
import { NoTranslate } from "../../../atoms/NoTranslate";
import { PartnerSummary } from "../../../../services/partnerAdmin";
import PartnerLinkFailureService, {
  LinkFailure,
  LinkFailureStage,
} from "../../../../services/partnerLinkFailures";
import { formatDay, partnerName } from "./partnerLabels";

interface PartnerLinkFailuresProps {
  customerId: string;
  entityId: string;
  partners: PartnerSummary[];
  onOpenPartner: (partnerId: string) => void;
  onChanged: () => void;
}

const STAGES: Record<LinkFailureStage, string> = {
  lookup: "The partner could not be read",
  attach: "The attachment could not be written",
};

const errorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || "Request failed";

/**
 * Signups that came through a partner link but could not be attached to the partner. Replaying attaches the account
 * as the link would have, at the signup date.
 */
const PartnerLinkFailures: React.FC<PartnerLinkFailuresProps> = ({
  customerId,
  entityId,
  partners,
  onOpenPartner,
  onChanged,
}) => {
  const [failures, setFailures] = useState<LinkFailure[]>([]);
  const [replaying, setReplaying] = useState<string | null>(null);

  const load = async () => {
    try {
      setFailures(await PartnerLinkFailureService.list(customerId, entityId));
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  useEffect(() => {
    if (customerId && entityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, entityId]);

  const open = failures.filter((failure) => failure.status === "open");
  if (open.length === 0) return null;

  const nameOf = (partnerId: string) => {
    const partner = partners.find((p) => p.partner_id === partnerId);
    return partner ? partnerName(partner) : partnerId;
  };

  const replay = async (failure: LinkFailure) => {
    setReplaying(failure.id);
    try {
      const result = await PartnerLinkFailureService.replay(
        customerId,
        entityId,
        failure.id
      );
      if (result.resolved) {
        toast.success(
          result.failure.resolution === "already_attached"
            ? "The account was already attached"
            : "Account attached to the partner"
        );
        onChanged();
      } else {
        toast.error(
          `Still failing: ${result.failure.last_error || "unknown cause"}`
        );
      }
      await load();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setReplaying(null);
    }
  };

  return (
    <section className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <p className="mb-3 text-sm font-semibold text-gray-900">
        Partner links that failed at signup{" "}
        <span className="tabular-nums">({open.length})</span>
      </p>
      <ul className="divide-y divide-amber-100 rounded-lg border border-amber-200 bg-white">
        {open.map((failure) => (
          <li
            key={failure.id}
            className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-start"
          >
            <div className="min-w-0 flex-1 text-xs text-gray-600 sm:basis-64">
              <p className="text-sm font-medium text-gray-900">
                <NoTranslate>
                  {failure.entity_name || failure.entity_id}
                </NoTranslate>
              </p>
              <p>
                {`Signed up on ${formatDay(
                  failure.created_at
                )} through the link of `}
                <NoTranslate>{nameOf(failure.partner_id)}</NoTranslate>
              </p>
              <p>
                {`${STAGES[failure.stage] ?? "The attachment failed"}: `}
                <NoTranslate>{failure.error}</NoTranslate>
              </p>
              {failure.attempts > 0 && (
                <p className="text-gray-500">
                  {`Replayed ${failure.attempts} time${
                    failure.attempts > 1 ? "s" : ""
                  }, last on ${formatDay(failure.last_attempt_at)}: `}
                  <NoTranslate>{failure.last_error}</NoTranslate>
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <CustomButton
                variant="secondary"
                size="xs"
                title="Partner"
                onClick={() => onOpenPartner(failure.partner_id)}
              />
              <CustomButton
                variant="primary"
                size="xs"
                title="Replay"
                disabled={replaying !== null}
                onClick={() => replay(failure)}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default PartnerLinkFailures;
